<template>
    <div>
        <v-container fluid class="pt-4 align-left">
            <v-row dense>

                <!-- INFO DIALOGS -->
                <v-col cols="12" class="pb-2">
                    <v-row fill-height>
                        <v-col cols="12" v-show="!userLocalizable">
                            <v-alert type="warning" prominent class="body-2 mb-0">
                                This data variable cannot be presented as a question.
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <v-row class="pl-0 pr-0" v-show="userLocalizable">
                <v-col cols="12" class="d-flex">

                    <!-- LOCALE SELECTOR -->
                    <div class="mt-0">
                        <div v-for="locale in dataVariableLocales" class="align-content-center mr-4">
                            <locale class="pa-0"
                                    v-if="selectedLocale"
                                    :editable="false"
                                    :data="locale"
                                    :clickable="true"
                                    :width="250"
                                    :active="locale.id === selectedLocale.id"
                                    active-style="arrow"
                                    high-light-color="#616161"
                                    color="#616161"
                                    @click="localeClicked"
                            ></locale>
                        </div>

                        <v-btn small text class="mt-3 ml-1" @click="addLocaleClicked">
                            <v-icon left>mdi-cog</v-icon>
                            Modify locales
                        </v-btn>

                    </div>

                    <!-- TRANSLATIONS OPTIONS -->
                    <div class="flex-grow-1">

                        <title-card title="Question" help="edit-datavariable-translations">

                            <template v-slot:buttons>

                                <v-tooltip top max-width="300" open-delay="200" color="primary" v-if="hasOptions">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" small text class="mr-1" @click="pasteOptionsClicked">
                                            <v-icon left>mdi-content-copy</v-icon>
                                            Paste options
                                        </v-btn>
                                    </template>
                                    Click here to paste in option texts i.e. from Word document, Excel etc..
                                </v-tooltip>

                                <v-tooltip top max-width="300" open-delay="200" color="transparent" v-if="hasOptions">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" small text class="mr-1" @click="syncOptionsClicked">
                                            <v-icon left>mdi-sync</v-icon>
                                            Sync options
                                        </v-btn>
                                    </template>
                                    <v-alert color="amber darken-2" prominent icon="mdi-alert" class="ma-0 body-2">
                                        <div>
                                            Click here to copy question options from the data variable options defined in the settings -tab.
                                            This will overwrite all question options!
                                        </div>
                                    </v-alert>
                                </v-tooltip>

                                <v-btn small text color="white" @click="previewQuestion" v-if="variable && variable.id">
                                    <v-icon left>mdi-eye</v-icon>
                                    Preview question
                                </v-btn>
                            </template>

                            <v-textarea
                                solo
                                placeholder="Please enter question text"
                                v-model="questionText"
                                :counter="200"
                                :required="false"
                                rows="2"
                                row-height="55"
                                auto-grow
                                @blur="emit()"
                            ></v-textarea>

                            <option-locale-editor
                                v-if="hasOptions"
                                :data-variable="variable"
                                :available-locales="locales"
                                :enable-add="false"
                                :enable-delete="false"
                                :edit-locale="selectedLocale"
                                @change="optionsChanged"
                            ></option-locale-editor>

                        </title-card>
                    </div>
                </v-col>
            </v-row>

        </v-container>

        <locale-picker
            v-model="localePickerModel"
            :locales="availableLocales"
            :selected="dataVariableLocales"
            :show-source="true"
            @enable="localeAdded"
            @disable="localeRemoved"
        ></locale-picker>

        <preview-question-dialog v-if="selectedLocale"
                                 :key="previewKey"
                                 v-model="previewQuestionDialogModel"
                                 :answer="null"
                                 :locale="selectedLocale"
                                 :variable="dataVariable"
        ></preview-question-dialog>

        <paste-options-dialog v-if="hasOptions"
                              v-model="pasteOptionDialogModel"
                              :min-options="dataVariable.options.length"
                              :max-options="dataVariable.options.length"
                              name="question"
                              @change="questionPasted"
        ></paste-options-dialog>

        <json v-if="false" :data="variable"/>

    </div>
</template>

<script>

import LocalePicker from '../../../../components/UI/LocalePicker';
import Locale from '../../../../components/UI/Locale';
import OptionLocaleEditor from '../OptionLocaleEditor';
import Json from 'vue-json-pretty';
import TitleCard from "../../../../components/UI/TitleCard";
import PreviewQuestionDialog from "../PreviewQuestionDialog";
import RangesTab from "../AgeRangeConfigurator";
import PasteOptionsDialog from "../PasteOptionsDialog";

export default {

    components: {
        PasteOptionsDialog,
        RangesTab,
        LocalePicker,
        Locale,
        OptionLocaleEditor,
        Json,
        TitleCard,
        PreviewQuestionDialog,
    },

    props: {
        dataVariable: {
            type: Object | null,
            required: true,
        },
        availableLocales: {
            type: Array | null,
            required: true
        },
        selectedLocales: {
            type: Array | null,
            required: true,
        }
    },
    data() {
        return {
            debug: false,
            selectedLocale: null,
            localePickerModel: false,
            variable: null,
            locales: null,
            previewQuestionDialogModel: false,
            previewKey: 1,
            pasteOptionDialogModel: false,
        }
    },
    watch: {
        // Async loading watch
        availableLocales(locales) {
            this.locales = JSON.parse(JSON.stringify(locales));
            this.selectFirstLocale();
        },
        // Async loading watch
        dataVariable(variable) {
            // Deep clone
            this.variable = JSON.parse(JSON.stringify(variable));
        }
    },

    beforeMount() {
        // If user is coming from the list page (both props are populated)
        if (this.dataVariable) {
            this.variable = JSON.parse(JSON.stringify(this.dataVariable));
        }

        if (this.availableLocales) {
            this.locales = JSON.parse(JSON.stringify(this.availableLocales));
        }

        this.selectFirstLocale();
    },

    computed: {

        userLocalizable() {
            if (this.variable) {
                return this.variable.user_localizable;
            }
        },

        questionText: {

            get() {
                let questionText = null;

                if (this.selectedLocale && this.locales && this.variable) {

                    // Find question text locale
                    const found = this.locales.find(variableLocale => {
                        return variableLocale.id === this.selectedLocale.id
                    });

                    if (!found) throw new Error('Question text locale could not be found (get)!');

                    questionText = this.variable.locales.find(l => l.locale_id === found.id).question_text;
                }
                return questionText;
            },
            set(questionText) {
                const found = this.variable.locales.find(variableLocale => {
                    return variableLocale.locale_id === this.selectedLocale.id
                });
                if (!found) throw new Error('Question text locale could not be found (set)!');
                found.question_text = questionText;
            }
        },

        // Find data variable locales based on locales question_text options
        dataVariableLocales() {

            let locales = [];
            if (this.variable && this.locales) {

                this.variable.locales.forEach(variableLocale => {

                        const found = this.locales.find(l => variableLocale.locale_id === l.id)

                        if (!found) {
                            throw new Error('Question option locale could not be found (' + variableLocale.locale_id + ')');
                        }
                        locales.push(found);
                    }
                );
            }

            return locales;
        },

        hasOptions() {
            return this.variable && (this.variable.type === 'radio' || this.variable.type === 'checkbox');
        }
    },

    methods: {

        questionPasted(options) {

            this.variable.options.sort((a, b) => {
                return a.order - b.order;
            })
                .forEach(option => {
                    let found = option.locales.find(locale => locale.locale_id === this.selectedLocale.id);

                    if (options.length > 0) {
                        found.option_text = options.splice(0, 1);
                    }
                });

            this.variable = Object.assign({}, this.variable);
        },

        pasteOptionsClicked() {
            this.pasteOptionDialogModel = true;
        },

        syncOptionsClicked() {
            if (this.hasOptions) {

                this.variable.options.forEach(option => {
                    let found = option.locales.find(locale => locale.locale_id === this.selectedLocale.id);
                    if (found) found.option_text = option.name;
                })

                this.emit();
            }
        },

        selectFirstLocale() {
            if (this.variable && this.locales) {
                const firstVariableLocale = this.variable.locales[0];
                this.selectedLocale = Object.assign(this.locales.find(locale => locale.id === firstVariableLocale.locale_id));
            }
        },

        addLocaleClicked() {
            this.localePickerModel = true;
        },

        localeClicked(locale) {
            if (this.selectedLocale.id === locale.id) {
                Bus.$emit('flash-message', {
                    type: 'info',
                    message: locale.code + " is already selected as indicated by the blue arrow!"
                });
            }
            this.selectedLocale = Object.assign({}, locale);
        },

        localeAdded({locale, source}) {
            // Deep clone
            let variable = JSON.parse(JSON.stringify(this.variable));

            const sourceLocale = variable.locales.find(l => l.locale_id === source.id);

            // Process question texts
            variable.locales.push({
                id: null,
                locale_id: locale.id,
                question_text: sourceLocale.question_text,
                data_variable_id: variable.id
            });

            // Process choices
            if (variable.options) {
                variable.options.forEach(option => {

                    const sourceOption = option.locales.find(l => l.locale_id === source.id);

                    option.locales.push({
                        id: null,
                        option_text: sourceOption.option_text,
                        option_id: option.id,
                        locale_id: locale.id
                    });
                });
            }

            this.variable = variable;
            this.emit();
        },

        localeRemoved(locale) {

            const localeId = locale.id;

            // Deep clone
            let variable = JSON.parse(JSON.stringify(this.variable));
            let existingLocales = JSON.parse(JSON.stringify(this.dataVariableLocales));

            // Remove selected locale and pick and next one if the current one gets deleted
            if (this.selectedLocale.id === localeId) {
                this.selectedLocale = existingLocales.find(l => l.id !== localeId);
            }

            // Check if this locale already exists
            let index = existingLocales.findIndex(l => l.id === locale.id);

            if (index >= 0) {
                let locales = [...variable.locales];
                locales.splice(index, 1);
                variable.locales = locales;
            }

            if (variable.options) {

                variable.options.forEach(option => {
                    const index = option.locales.findIndex(l => l.locale_id === parseInt(localeId));
                    if (index >= 0) option.locales.splice(index, 1);
                });
            }

            this.variable = variable;
            this.emit();
        },

        optionsChanged(options) {
            this.variable.options = options;
            this.emit();
        },

        previewQuestion() {
            this.previewKey++;

            this.$nextTick(() => {
                this.previewQuestionDialogModel = true;
            });
        },

        emit() {
            this.$emit('change', Object.assign({}, this.variable));
        }
    }
}
</script>
